import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, FileType2, Download, Eye, Lock, Loader2, ExternalLink, Link2 } from "lucide-react";
import { api, API, COLORS } from "@/lib/api";
import { toast } from "sonner";

const mediaUrl = (ref) => `${API}/media/${ref}`;

const DocCard = ({ doc }) => {
  if (doc.kind === "link") {
    return (
      <a
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`recurso-doc-${doc.id}`}
        className="group flex flex-col justify-between rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-brand-magenta transition-all p-5 min-h-[160px]"
      >
        <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "#FDE7FA" }}>
          <Link2 size={20} className="text-brand-magenta" />
        </span>
        <div className="mt-4">
          <h3 className="font-heading text-base font-bold tracking-tight text-brand-ink leading-tight">{doc.title}</h3>
          <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-magenta group-hover:gap-2.5 transition-all">
            Abrir enlace <ExternalLink size={14} />
          </span>
        </div>
      </a>
    );
  }

  if (doc.kind === "doc") {
    return (
      <div data-testid={`recurso-doc-${doc.id}`} className="group flex flex-col justify-between rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-brand-magenta transition-all p-5 min-h-[160px]">
        <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "#E6F0FF" }}>
          <FileType2 size={20} className="text-[#2B579A]" />
        </span>
        <div className="mt-4">
          <h3 className="font-heading text-base font-bold tracking-tight text-brand-ink leading-tight">{doc.title}</h3>
          <a
            href={mediaUrl(doc.pdf_ref)}
            download
            data-testid={`recurso-download-${doc.id}`}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-magenta group-hover:gap-2.5 transition-all"
          >
            Descargar documento <Download size={14} />
          </a>
        </div>
      </div>
    );
  }

  const pdf = mediaUrl(doc.pdf_ref);
  const cover = mediaUrl(doc.cover_ref);
  return (
    <div
      data-testid={`recurso-doc-${doc.id}`}
      className="group rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-brand-magenta transition-all"
      style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
    >
      <a href={pdf} target="_blank" rel="noopener noreferrer" className="block relative aspect-[4/5] overflow-hidden bg-[#F6F6F6]">
        <img src={cover} alt={doc.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
      </a>
      <div className="p-4">
        <h3 className="font-heading text-base font-bold tracking-tight text-brand-ink leading-tight">{doc.title}</h3>
        <div className="mt-3 flex gap-2">
          <a
            href={pdf}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`recurso-read-${doc.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-white px-3 py-2 rounded-full bg-brand-ink hover:bg-brand-magenta transition-colors"
          >
            <Eye size={14} /> Ver
          </a>
          <a
            href={pdf}
            download
            data-testid={`recurso-download-${doc.id}`}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#E5E5E5] text-brand-ink hover:border-brand-magenta hover:text-brand-magenta transition-colors"
            aria-label="Descargar"
          >
            <Download size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

const Grid = ({ docs }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
    {docs.map((d) => (
      <DocCard key={d.id} doc={d} />
    ))}
  </div>
);

const RecursosArchive = ({ category, title, intro, accentKey = "magenta", groups = null, requirePassword = false }) => {
  const accent = COLORS[accentKey];
  const [docs, setDocs] = useState([]);
  const [meta, setMeta] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [unlocked, setUnlocked] = useState(!requirePassword);
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);

  const fetchDocs = async (pw = "") => {
    const url = `/resources/${category}${pw ? `?password=${encodeURIComponent(pw)}` : ""}`;
    const r = await api.get(url);
    setDocs(r.data);
    setLoaded(true);
  };

  useEffect(() => {
    api.get(`/resource-meta/${category}`).then((r) => setMeta(r.data || {})).catch(() => {});
    if (!requirePassword) {
      fetchDocs().catch(() => setLoaded(true));
    }
  }, [category, requirePassword]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      await fetchDocs(password);
      setUnlocked(true);
    } catch (err) {
      toast.error("Contraseña incorrecta. Intenta de nuevo.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div data-testid={`recursos-${category}`}>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]" style={{ background: `linear-gradient(120deg, ${accent} 0%, ${COLORS.orange} 100%)` }}>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link to="/recursos" data-testid="subpage-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6">
            <ArrowLeft size={16} /> Recursos
          </Link>
          <span className="inline-block w-16 h-1.5 rounded-sm mb-6 bg-white" />
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">{title}</h1>
          <p className="mt-5 text-lg md:text-xl text-white/90 font-medium max-w-2xl">{intro}</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
        {!unlocked ? (
          <motion.form
            onSubmit={submitPassword}
            data-testid="recursos-password-form"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto text-center border border-[#E5E5E5] rounded-2xl p-8"
          >
            <span className="w-14 h-14 rounded-full flex items-center justify-center mx-auto" style={{ background: "#FDE7FA" }}>
              <Lock size={24} className="text-brand-magenta" />
            </span>
            <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-brand-ink">Contenido para clubs</h2>
            <p className="mt-2 text-brand-ink/70">Ingresa la contraseña para acceder a los recursos exclusivos de la red de clubs.</p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="recursos-password-input"
              placeholder="Contraseña"
              className="mt-6 w-full border border-[#E5E5E5] rounded-full px-5 py-3 outline-none focus:border-brand-magenta text-center"
            />
            <button
              type="submit"
              disabled={checking}
              data-testid="recursos-password-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-brand-magenta text-white font-semibold px-6 py-3 rounded-full hover:bg-brand-orange transition-colors disabled:opacity-60"
            >
              {checking ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              {checking ? "Verificando…" : "Entrar"}
            </button>
          </motion.form>
        ) : !loaded ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand-magenta" /></div>
        ) : docs.length === 0 && !groups ? (
          <div className="text-center py-10">
            <FileText size={30} className="text-brand-ink/30 mx-auto" />
            <p className="mt-3 text-brand-ink/50">Aún no hay documentos publicados aquí.</p>
          </div>
        ) : groups ? (
          <div className="space-y-14">
            {groups.map((g) => {
              const items = docs.filter((d) => d.group === g.value);
              if (items.length === 0 && !meta[g.value]) return null;
              return (
                <div key={g.value} data-testid={`recursos-group-${g.value}`}>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-ink">{g.label}</h2>
                  {meta[g.value] && <p className="mt-2 text-brand-ink/70 leading-relaxed max-w-3xl">{meta[g.value]}</p>}
                  {items.length > 0 ? (
                    <div className="mt-5"><Grid docs={items} /></div>
                  ) : (
                    <p className="mt-4 text-sm text-brand-ink/40">Aún no hay recursos en esta sección.</p>
                  )}
                </div>
              );
            })}
            {(() => {
              const known = groups.map((g) => g.value);
              const others = docs.filter((d) => !known.includes(d.group));
              if (others.length === 0) return null;
              return (
                <div data-testid="recursos-group-otros">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-ink mb-5">Otros recursos</h2>
                  <Grid docs={others} />
                </div>
              );
            })()}
          </div>
        ) : (
          <Grid docs={docs} />
        )}
      </div>
    </div>
  );
};

export default RecursosArchive;
